import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Building2, Pencil, Trash2, Tag, Zap, Calendar, ChevronDown, ChevronUp, Calculator, AlertCircle } from 'lucide-react';
import { RosetteIcon } from './PartnerBadges';
import { useCart } from '../context/CartContext';
import { isPartnerClickable, PARTNER_ACTIVE_CLASSES, PARTNER_DISABLED_CLASSES, PARTNER_DISABLED_TOOLTIP } from '../lib/partnerPolicy';

const AVAIL_LABELS = { available: 'Disponible', unavailable: 'Indisponible', unknown: 'N/C' };
const AVAIL_COLORS = { available: 'text-green-700', unavailable: 'text-red-600', unknown: 'text-gray-400' };

const NATURE_LABELS = {
  PERMANENTE: 'Permanente', PRECOMMANDE: 'Précommande', LANCEMENT: 'Lancement',
  PROMOTION: 'Promotion', SOLDES: 'Soldes', PROMO_FLASH: 'Promo flash',
  DESTOCKAGE: 'Déstockage', EVENEMENTIELLE: 'Événementielle'
};
const NATURE_COLORS = {
  PERMANENTE: 'bg-emerald-50 border-emerald-200', PRECOMMANDE: 'bg-blue-50 border-blue-200',
  LANCEMENT: 'bg-violet-50 border-violet-200', PROMOTION: 'bg-red-50 border-red-200',
  SOLDES: 'bg-amber-50 border-amber-200', PROMO_FLASH: 'bg-orange-50 border-orange-200',
  DESTOCKAGE: 'bg-yellow-50 border-yellow-200', EVENEMENTIELLE: 'bg-pink-50 border-pink-200'
};
const STATUS_COLORS = { PLANIFIED: 'bg-blue-100 text-blue-700', ACTIVE: 'bg-green-100 text-green-800', ARCHIVED: 'bg-gray-200 text-gray-500' };
const STATUS_LABELS = { PLANIFIED: 'Planifiée', ACTIVE: 'Active', ARCHIVED: 'Archivée' };

const fmtPrice = (v) => v != null ? `${Number(v).toFixed(2).replace('.', ',')}\u00A0\u20AC` : '\u2014';
const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
};

function palierLabel(rule, mechEffectType) {
  const et = rule.effect_type || mechEffectType;
  const v = rule.effect_value || 0;
  const mq = rule.min_quantity || 1;
  switch (et) {
    case 'FREE_UNITS': return `${Math.round(v)} unité${v > 1 ? 's' : ''} offerte${v > 1 ? 's' : ''} dès ${mq} unité${mq > 1 ? 's' : ''} commandée${mq > 1 ? 's' : ''}`;
    case 'DISCOUNT_PERCENT': return `${v}% de remise dès ${mq} unité${mq > 1 ? 's' : ''} commandée${mq > 1 ? 's' : ''}`;
    case 'UNIT_PRICE_OVERRIDE': return `prix unitaire à ${v.toFixed(2)}\u00A0\u20AC dès ${mq} unité${mq > 1 ? 's' : ''} commandée${mq > 1 ? 's' : ''}`;
    default: return `${v} dès ${mq}`;
  }
}

// Compute status from dates (same logic as backend compute_mechanic_status)
function computeStatus(mech) {
  const now = new Date();
  const dStart = mech.date_debut;
  const dEnd = mech.date_fin;
  if (!dStart && !dEnd) return 'ACTIVE';
  if (dStart) {
    const s = String(dStart);
    const startDt = s.includes('T') ? new Date(s.replace('Z', '+00:00')) : new Date(s + 'T00:00:00+00:00');
    if (!isNaN(startDt) && now < startDt) return 'PLANIFIED';
  }
  if (dEnd) {
    const e = String(dEnd);
    const endDt = e.includes('T') ? new Date(e.replace('Z', '+00:00')) : new Date(e + 'T23:59:59+00:00');
    if (!isNaN(endDt) && now > endDt) return 'ARCHIVED';
  }
  return 'ACTIVE';
}

/**
 * DistributionCard V4
 * Retailer: tableau logistique compact + mécaniques ACTIVE uniquement, pas de badges status, pas de titres superflus
 * Admin/Partner: affichage complet avec status
 */
const DistributionCard = ({ dist, idx, isPartner, isOwn, canManage = false, onEdit, onDelete, userRole = 'magasin', defaultExpanded = true, productId, productName, productImage }) => {
  const mechanics = Array.isArray(dist.promotional_mechanics) ? dist.promotional_mechanics : [];
  const isRetailer = userRole === 'retailer';
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { addItem } = useCart();

  const handleAddToSim = (e) => {
    e.stopPropagation();
    addItem({
      product_id: productId,
      product_name: productName || 'Produit',
      product_image: productImage,
      distribution_id: dist.id,
      partner_id: dist.partner_id,
      partner_nom: dist.partner_nom || 'Distributeur',
      base_price: dist.purchase_price || 0,
      quantity: Math.max(dist.multiple_commande || 1, dist.minimum_commande || 1),
      min_qty: dist.minimum_commande,
      max_qty: dist.maximum_commande,
      multiple: dist.multiple_commande,
    });
  };
  
  // Filter by computed status
  const visibleMechanics = mechanics.filter(m => {
    const st = m.computed_status || computeStatus(m);
    if (isRetailer) return st === 'ACTIVE';
    return true;
  });

  return (
    <div className={`rounded-xl border-2 ${isOwn ? 'border-blue-300 bg-blue-50/20' : 'border-slate-200 bg-white'} shadow-sm overflow-hidden`} data-testid={`dist-card-${idx}`}>
      
      {/* HEADER — clickable accordion */}
      <button type="button" onClick={() => setExpanded(!expanded)}
        className={`w-full px-4 py-3 flex items-center justify-between gap-2 text-left ${isOwn ? 'bg-blue-50 border-b border-blue-200' : 'bg-slate-50 border-b border-slate-200'}`}
        data-testid={`dist-toggle-${idx}`}>
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
          {isPartnerClickable(dist) && dist.partner_slug ? (
            <Link to={`/partner/${dist.partner_slug}`} onClick={(e) => e.stopPropagation()} className={`text-sm font-bold truncate ${PARTNER_ACTIVE_CLASSES}`}>
              {dist.partner_nom || 'Distributeur'}
            </Link>
          ) : (
            <span className={`text-sm font-bold truncate ${dist.partner_slug ? PARTNER_DISABLED_CLASSES : 'text-slate-900'}`} title={dist.partner_slug && !isPartnerClickable(dist) ? PARTNER_DISABLED_TOOLTIP : undefined}>{dist.partner_nom || 'Distributeur'}</span>
          )}
          {dist.partner_is_commercial && <RosetteIcon className="w-4 h-4 flex-shrink-0" />}
          {isOwn && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-200 text-blue-800 font-semibold">Vous</span>}
          <span className="text-xs text-slate-400 ml-1">{fmtPrice(dist.purchase_price)}/u</span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isPartner && isOwn && canManage && (
            <>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-100" onClick={(e) => { e.stopPropagation(); onEdit(dist); }} data-testid={`dist-edit-btn-${idx}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:bg-red-100" onClick={(e) => { e.stopPropagation(); onDelete(dist); }} data-testid={`dist-delete-btn-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </>
          )}
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
      <div className="p-4 space-y-4">
        {/* TABLEAU LOGISTIQUE COMPACT */}
        <div className="overflow-x-auto" data-testid={`dist-logistics-table-${idx}`}>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase pb-1.5 pr-3 whitespace-nowrap">Réf Distrib</th>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase pb-1.5 pr-3 whitespace-nowrap">Prix Achat</th>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase pb-1.5 pr-3 whitespace-nowrap">Minimum commande</th>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase pb-1.5 pr-3 whitespace-nowrap">Multiple commande</th>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase pb-1.5 pr-3 whitespace-nowrap">Colisage</th>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase pb-1.5 whitespace-nowrap">Disponibilité</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-1.5 pr-3 font-mono text-xs text-gray-600">{dist.supplier_reference || '\u2014'}</td>
                <td className="py-1.5 pr-3 font-semibold text-gray-900">{fmtPrice(dist.purchase_price)}</td>
                <td className="py-1.5 pr-3 text-gray-700">{dist.minimum_commande || '\u2014'}</td>
                <td className="py-1.5 pr-3 text-gray-700">{dist.multiple_commande ? `x${dist.multiple_commande}` : '\u2014'}</td>
                <td className="py-1.5 pr-3 text-gray-700">{dist.colisage || '\u2014'}</td>
                <td className={`py-1.5 font-medium ${AVAIL_COLORS[dist.availability_status] || 'text-gray-400'}`}>
                  {AVAIL_LABELS[dist.availability_status] || 'N/C'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* MÉCANIQUES PROMOTIONNELLES */}
        {visibleMechanics.length > 0 && (
          <div className="space-y-2" data-testid={`dist-mechanics-${idx}`}>
            {visibleMechanics.map((mech, mi) => {
              const nat = mech.nature_offre || 'PROMOTION';
              const status = mech.computed_status || computeStatus(mech);
              const rules = (mech.pricing_rules || []).sort((a, b) => (a.min_quantity || 1) - (b.min_quantity || 1));
              const isPermanente = nat === 'PERMANENTE';
              const natLabel = NATURE_LABELS[nat] || nat;
              const title = mech.title || natLabel;

              // Date segment for inline display
              const dateSeg = isPermanente
                ? 'Offre Permanente'
                : (mech.date_debut || mech.date_fin)
                  ? `${fmtDate(mech.date_debut) || '?'}${mech.date_fin ? ` \u2192 ${fmtDate(mech.date_fin)}` : ''}`
                  : null;
              
              return (
                <div key={mech.id || mi} className={`px-3 py-2.5 rounded-lg border ${NATURE_COLORS[nat] || 'bg-gray-50 border-gray-200'}`} data-testid={`mechanic-card-${idx}-${mi}`}>
                  {/* SINGLE LINE: [Nature] — [Titre] — [Dates / Offre Permanente] */}
                  <div className="flex items-center gap-2 flex-wrap" data-testid={`mech-inline-${idx}-${mi}`}>
                    <Tag className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                    <span className="text-xs font-bold text-gray-900" data-testid={`mech-title-${idx}-${mi}`}>
                      {natLabel}
                    </span>
                    <span className="text-xs text-gray-400">&mdash;</span>
                    <span className="text-xs font-semibold text-gray-800">{title}</span>
                    {dateSeg && (
                      <>
                        <span className="text-xs text-gray-400">&mdash;</span>
                        <span className={`text-xs font-bold ${isPermanente ? 'text-emerald-700' : 'text-gray-700'}`} data-testid={`mech-date-seg-${idx}-${mi}`}>
                          {dateSeg}
                        </span>
                      </>
                    )}
                    {/* Status badge for admin/partner only */}
                    {!isRetailer && (
                      <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded flex-shrink-0 ${STATUS_COLORS[status] || ''}`}>
                        {STATUS_LABELS[status] || status}
                      </span>
                    )}
                  </div>
                  
                  {/* Paliers */}
                  {rules.length > 0 && (
                    <div className="space-y-0.5 mt-1.5 ml-6">
                      {rules.map((rule, ri) => (
                        <p key={rule.id || ri} className="text-xs text-gray-700 leading-relaxed" data-testid={`rule-label-${idx}-${mi}-${ri}`}>
                          {palierLabel(rule, mech.effect_type)}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ADD TO CART — Removed: use ComparisonTable per-row buttons instead (Phase 4E) */}
      </div>
      )}
    </div>
  );
};

export default DistributionCard;
